import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { ApiResult } from '@synergy/api';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';

import * as ProductDetailAttributesActions from '../attributes/store/product-detail-attributes-actions';
import * as ProductDetailOverviewActions from '../overview/store/product-detail-overview-actions';
import * as ProductDetailPricesActions from '../prices/store/product-detail-prices-actions';
import { Api } from '../../../../../api';
import { AppState } from '../../../../../store/models/app-state';
import { ErrorMessage } from '../../../../../common/models/enums/error-message';
import { ReduxDispatch } from '../../../../../store/use-thunk-dispatch';
import { productDetailIsValidSelector } from './product-detail-selectors';

export const PRODUCT_DETAIL_RESET = 'PRODUCT_DETAIL_RESET';
export const PRODUCT_DETAIL_SAVE = 'PRODUCT_DETAIL_SAVE';
export const PRODUCT_DETAIL_ACTIVATE = 'PRODUCT_DETAIL_ACTIVATE';
export const PRODUCT_DETAIL_DEACTIVATE = 'PRODUCT_DETAIL_DEACTIVATE';
export const PRODUCT_DETAIL_DELETE = 'PRODUCT_DETAIL_DELETE';

export const resetProductDetails = () => ({ type: PRODUCT_DETAIL_RESET });

export const activateProduct = () => {
    return async (dispatch: ReduxDispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: PRODUCT_DETAIL_ACTIVATE });

        const id = getState().product.detail.overview.productDetail?.id;
        if (!id) {
            return;
        }

        const response = await api.product.detail.activate(id);
        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.articleActivated')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const deactivateProduct = () => {
    return async (dispatch: ReduxDispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: PRODUCT_DETAIL_DEACTIVATE });

        const id = getState().product.detail.overview.productDetail?.id;
        if (!id) {
            return;
        }

        const response = await api.product.detail.deactivate(id);
        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.articleDeactivated')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const deleteProduct = (productId?: number) => {
    return async (dispatch: ReduxDispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: PRODUCT_DETAIL_DELETE });

        const id = productId ?? getState().product.detail.overview.productDetail?.id;
        if (!id) {
            return;
        }

        const response = await api.product.detail.delete(id);
        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.articleDeleted')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const saveProductDetails = () => {
    return async (dispatch: ReduxDispatch, getState: () => AppState) => {
        dispatch({ type: PRODUCT_DETAIL_SAVE });

        const state = getState();
        const isAllValid = productDetailIsValidSelector(state);
        const productDetailState = state.product.detail;

        if (!isAllValid) {
            if (productDetailState.overview.productDetail?.id) {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.formErrors')));
            }
            return;
        }

        const promises: Promise<ApiResult<unknown>>[] = [];

        if (productDetailState.overview.isDirty) {
            promises.push(dispatch(ProductDetailOverviewActions.saveProductDetail()));
        }
        if (productDetailState.attributes.isDirty) {
            promises.push(dispatch(ProductDetailAttributesActions.saveAttributes()));
        }
        if (productDetailState.prices.isDirty) {
            promises.push(dispatch(ProductDetailPricesActions.saveProductDetailPrices()));
        }

        const results = await Promise.all(promises);
        if (results.every(result => result?.success ?? false)) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.saved')));
        } else {
            if (
                results.every(
                    result =>
                        (!result?.success && result.error?.title === ErrorMessage.NonUniquePackingUnitQuantity) ?? false
                )
            ) {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.nonUniquePackingUnitQuantity')));
            } else if (
                results.some(
                    result =>
                        !result?.success &&
                        result.error?.title === ErrorMessage.NonUniqueProperties &&
                        'number' === (result.error.context as { field?: string } | undefined)?.field
                )
            ) {
                // The number field says which supplier holds it; this points
                // at the field, because the tab in front may be another one.
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.productNumberTaken')));
            } else {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
            }
        }
    };
};
